import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { MessageCircle, Send } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/SegmentedControl";
import { Textarea } from "@/components/ui/Textarea";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { getApiErrorMessage } from "@/api/client";
import { CHAT_CATEGORIES } from "@/features/support/categories";
import { useReplyToTicket, useTicket, useTicketList, useUpdateTicket } from "@/features/support/useSupport";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { SocketEvents } from "@/sockets/socket";
import { toast } from "@/store/toast.store";
import { cn } from "@/utils/cn";
import { formatDateTime } from "@/utils/format";
import type { SupportTicket, TicketMessage, TicketSource } from "@/types/domain";

type SourceFilter = "ALL" | TicketSource;
type ChatState = "ACTIVE" | "ENDED";

const LIST_LIMIT = 50;
const CHAT_CATEGORY_QUERY = CHAT_CATEGORIES.join(",");

/** The rider or driver a chat is with. Older backends only send the rider fields. */
const personOf = (t: SupportTicket) => t.requester ?? { type: "RIDER" as const, name: t.customerName ?? null, phone: t.customerPhone ?? null };
const nameOf = (t: SupportTicket) => {
  const p = personOf(t);
  return p.name ?? p.phone ?? (p.type === "DRIVER" ? "Driver" : "Rider");
};
const lastLine = (t: SupportTicket) => (t.messages.length ? t.messages[t.messages.length - 1].text : (t.detail ?? ""));
const timeOnly = (iso: string) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

/** Payload of `admin:support:message` — what the Live chat tab needs from it. */
interface SupportPush {
  ticketId: string;
  chat?: boolean;
  created?: boolean;
  ended?: boolean;
  customerName?: string;
  driverName?: string;
  message?: { senderRole?: string; text?: string };
}

export default function LiveChat({ viewSwitch }: { viewSwitch: ReactNode }) {
  const [source, setSource] = useState<SourceFilter>("ALL");
  const [state, setState] = useState<ChatState>("ACTIVE");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      limit: LIST_LIMIT,
      category: CHAT_CATEGORY_QUERY,
      status: state === "ACTIVE" ? "OPEN,WAITING" : "RESOLVED",
      source: source === "ALL" ? undefined : source,
    }),
    [source, state],
  );
  const list = useTicketList(query);
  const chats = list.data?.items ?? [];

  // A message from someone whose chat isn't the one on screen: say so (the list itself refreshes through
  // the support sync). The admin's own replies and ended-chat notices are not announced.
  useSocketEvent<SupportPush>(
    SocketEvents.AdminSupportMessage,
    (p) => {
      if (!p.chat || p.ended || !p.message || p.message.senderRole === "ADMIN" || p.ticketId === selectedId) return;
      const who = p.customerName ?? p.driverName ?? (p.created ? "Someone" : "A chat");
      toast.info(p.created ? `New live chat from ${who}` : `${who} sent a message`);
    },
    [selectedId],
  );

  const sourceOptions: SegmentedControlOption<SourceFilter>[] = [
    { value: "ALL", label: "All" },
    { value: "RIDER", label: "Riders" },
    { value: "DRIVER", label: "Drivers" },
  ];
  const stateOptions: SegmentedControlOption<ChatState>[] = [
    { value: "ACTIVE", label: "Active", count: state === "ACTIVE" ? chats.length : undefined },
    { value: "ENDED", label: "Ended" },
  ];

  return (
    <PageContainer title="Support" description="Live chats with riders and drivers. Replies reach their app instantly." actions={viewSwitch}>
      <div className="card flex h-[calc(100vh-13rem)] min-h-[30rem] overflow-hidden p-0">
        {/* conversations */}
        <aside className="flex w-80 shrink-0 flex-col border-r border-border">
          <div className="flex flex-col gap-2 border-b border-border p-3">
            <SegmentedControl options={stateOptions} value={state} onChange={(s) => { setState(s); setSelectedId(null); }} />
            <SegmentedControl options={sourceOptions} value={source} onChange={(s) => { setSource(s); setSelectedId(null); }} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.isLoading && <LoadingState />}
            {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}
            {!list.isLoading && !list.isError && chats.length === 0 && (
              <EmptyState
                icon={<MessageCircle className="h-8 w-8" />}
                title={state === "ACTIVE" ? "No active chats" : "No ended chats"}
                description={state === "ACTIVE" ? "A rider or driver's message starts one here." : undefined}
              />
            )}
            <ul>
              {chats.map((t) => (
                <ConversationRow key={t.id} t={t} active={t.id === selectedId} onSelect={() => setSelectedId(t.id)} />
              ))}
            </ul>
          </div>
        </aside>

        {/* the open conversation */}
        <section className="flex min-w-0 flex-1 flex-col">
          {selectedId ? <Conversation key={selectedId} id={selectedId} /> : (
            <EmptyState icon={<MessageCircle className="h-8 w-8" />} title="Select a chat" description="Pick a conversation on the left to read and reply." />
          )}
        </section>
      </div>
    </PageContainer>
  );
}

function ConversationRow({ t, active, onSelect }: { t: SupportTicket; active: boolean; onSelect: () => void }) {
  const needsReply = t.status === "OPEN";
  const p = personOf(t);
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-start gap-3 border-b border-border px-3 py-3 text-left transition-colors",
          active ? "bg-teal-500/10" : "hover:bg-cream-200/60",
        )}
      >
        <Avatar name={nameOf(t)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={cn("truncate text-sm", needsReply ? "font-semibold text-ink" : "font-medium text-ink")}>{nameOf(t)}</p>
            <span className="shrink-0 text-[11px] text-ink-faint">{timeOnly(t.updatedAt)}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <Badge tone={p.type === "DRIVER" ? "gold" : "neutral"}>{p.type === "DRIVER" ? "Driver" : "Rider"}</Badge>
            {needsReply && <span className="text-[11px] font-medium text-gold-700">Needs reply</span>}
          </div>
          <p className={cn("mt-1 truncate text-xs", needsReply ? "text-ink" : "text-ink-faint")}>{lastLine(t)}</p>
        </div>
      </button>
    </li>
  );
}

function Conversation({ id }: { id: string }) {
  const ticket = useTicket(id);
  const reply = useReplyToTicket();
  const update = useUpdateTicket();
  const [text, setText] = useState("");
  const [confirmEnd, setConfirmEnd] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const t = ticket.data;
  const messageCount = t?.messages.length ?? 0;

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messageCount]);

  if (ticket.isLoading) return <LoadingState />;
  if (ticket.isError || !t) return <ErrorState error={ticket.error} onRetry={() => ticket.refetch()} />;

  const ended = t.status === "RESOLVED";
  const p = personOf(t);

  function send() {
    const body = text.trim();
    if (!t || !body || ended) return;
    reply.mutate(
      { id: t.id, text: body },
      {
        onSuccess: () => setText(""),
        onError: (err) => toast.error(getApiErrorMessage(err)),
      },
    );
  }

  function endChat() {
    if (!t) return;
    update.mutate(
      { id: t.id, status: "RESOLVED", resolution: "Chat ended by support" },
      {
        onSuccess: () => {
          setConfirmEnd(false);
          toast.success("Chat ended");
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      },
    );
  }

  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={nameOf(t)} size={40} />
          <div className="min-w-0">
            <p className="truncate font-semibold text-ink">{nameOf(t)}</p>
            <p className="truncate text-xs text-ink-muted">
              {p.type === "DRIVER" ? "Driver" : "Rider"}
              {p.phone ? ` · ${p.phone}` : ""}
              {p.type === "DRIVER" && t.requesterVehicle ? ` · ${t.requesterVehicle.registrationNumber}` : ""}
              {` · started ${formatDateTime(t.createdAt)}`}
            </p>
          </div>
        </div>
        {ended ? (
          <Badge tone="teal">Ended</Badge>
        ) : confirmEnd ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-muted">End this chat?</span>
            <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => setConfirmEnd(false)}>
              Keep open
            </Button>
            <Button size="sm" variant="secondary" loading={update.isPending} onClick={endChat}>
              End chat
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setConfirmEnd(true)}>
            End chat
          </Button>
        )}
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-cream-100 px-4 py-4">
        {t.messages.length === 0 && <p className="py-8 text-center text-sm text-ink-faint">No messages yet.</p>}
        {t.messages.map((m, i) => (
          <Bubble key={m._id ?? i} m={m} />
        ))}
        {ended && <p className="py-2 text-center text-xs text-ink-faint">Chat ended{t.resolvedAt ? ` · ${formatDateTime(t.resolvedAt)}` : ""}</p>}
        <div ref={endRef} />
      </div>

      <footer className="border-t border-border p-3">
        {ended ? (
          <p className="px-1 text-sm text-ink-muted">This chat has ended. If they write again, a new chat starts.</p>
        ) : (
          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              value={text}
              maxLength={1000}
              placeholder="Write a reply — Enter to send, Shift + Enter for a new line"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <Button onClick={send} loading={reply.isPending} disabled={!text.trim()}>
              <Send className="h-4 w-4" />
              Send
            </Button>
          </div>
        )}
      </footer>
    </>
  );
}

function Bubble({ m }: { m: TicketMessage }) {
  const fromAdmin = m.senderRole === "ADMIN";
  return (
    <div className={cn("flex", fromAdmin ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-2xl px-3.5 py-2 text-sm",
          fromAdmin ? "rounded-br-sm bg-teal-500 text-cream-50" : "rounded-bl-sm border border-border bg-cream-50 text-ink",
        )}
      >
        {fromAdmin && m.senderName && <p className="mb-0.5 text-[11px] font-medium opacity-80">{m.senderName}</p>}
        <p className="whitespace-pre-wrap break-words">{m.text}</p>
        <p className={cn("mt-1 text-right text-[10px]", fromAdmin ? "text-cream-50/70" : "text-ink-faint")}>{timeOnly(m.sentAt)}</p>
      </div>
    </div>
  );
}
